import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../../lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  program_id: "",
  program_name: "",
  session_type: "online",
  preferred_date: "",
  preferred_time: "",
  notes: "",
};

export const Booking = () => {
  const [form, setForm] = useState(initial);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data.programs || [])).catch(() => {});
  }, []);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onProgram = (e) => {
    const id = e.target.value;
    const p = programs.find((x) => x.id === id);
    setForm({ ...form, program_id: id, program_name: p ? p.name : "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.program_id || !form.preferred_date) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/booking", form);
      toast.success("Booking request received. We'll confirm by email shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 md:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">
            Book a session
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0B2B40] tracking-tight leading-tight font-light">
            Schedule a 1:1 session — <span className="italic">online or in-person.</span>
          </h2>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="booking-form"
          onSubmit={submit}
          className="bg-white p-8 md:p-12 border border-slate-100"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Full name *</label>
              <input data-testid="booking-name-input" className="input-underline" value={form.name} onChange={handle("name")} />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Email *</label>
              <input data-testid="booking-email-input" type="email" className="input-underline" value={form.email} onChange={handle("email")} />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Phone</label>
              <input data-testid="booking-phone-input" className="input-underline" value={form.phone} onChange={handle("phone")} />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Program *</label>
              <select data-testid="booking-program-select" className="input-underline" value={form.program_id} onChange={onProgram}>
                <option value="">Select a program</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Session type *</label>
              <select data-testid="booking-type-select" className="input-underline" value={form.session_type} onChange={handle("session_type")}>
                <option value="online">Online</option>
                <option value="in-person">In-person</option>
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Preferred date *</label>
              <input data-testid="booking-date-input" type="date" className="input-underline" value={form.preferred_date} onChange={handle("preferred_date")} />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Preferred time</label>
              <input data-testid="booking-time-input" type="time" className="input-underline" value={form.preferred_time} onChange={handle("preferred_time")} />
            </div>
          </div>
          <div className="mt-6">
            <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Notes</label>
            <textarea data-testid="booking-notes-input" className="input-underline" rows={3} value={form.notes} onChange={handle("notes")} />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit-button"
            className="mt-10 bg-[#0B2B40] text-white hover:bg-[#1D4E89] disabled:opacity-60 px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
          >
            {loading ? "Sending..." : "Confirm Booking Request"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Booking;
